import { formatCurrency } from "@/lib/currency-utils";
import {
  getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions,
  useAdminOrganizationGetCurrentAndActiveSemester,
} from "@/orval/admin-organization";
import { adminTeacherPaymentsGetTeacherPayments } from "@/orval/admin-teacher-payments";
import type { AdminTeacherPaymentClassDTO, AdminTeacherPaymentDTO } from "@/orval/model";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/data-table/DataTable";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import { Form } from "@/components/form/form";
import { PortalPageHeader } from "@/components/portal-page-header";
import { optionalSemesterSearchSchema } from "@/lib/route-search";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/teacher-payments")({
  component: RouteComponent,
  validateSearch: optionalSemesterSearchSchema,
  loader: async ({ params, location, ...loaderContext }) => {
    const search = optionalSemesterSearchSchema.parse(location.search);
    let semesterId = search.semesterId;
    if (semesterId === undefined) {
      const semesters = await loaderContext.context.queryClient.ensureQueryData(
        getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions(params.organizationId),
      );
      semesterId = semesters?.nextSemesterId ?? semesters?.activeSemesterId ?? undefined;
    }

    if (!semesterId) {
      return;
    }

    const queryOptions = buildDataTableQueryOptions({});
    return ensureRouteQueriesData(loaderContext, [
      {
        queryKey: ["teacher-payments", params.organizationId, semesterId, queryOptions, []],
        queryFn: () => adminTeacherPaymentsGetTeacherPayments(params.organizationId, semesterId, [], queryOptions),
      },
    ]);
  },
});

export function TeacherPaymentClasses({ classes }: { classes: AdminTeacherPaymentClassDTO[] }) {
  return (
    <div className="space-y-2 md:space-y-1">
      {classes.map((classPayment) => (
        <div
          key={classPayment.classId}
          className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
        >
          <span className="min-w-0 break-words font-medium leading-snug sm:flex-1">{classPayment.className}</span>
          <span className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-sm text-muted-foreground sm:justify-end sm:whitespace-nowrap">
            <span>
              {classPayment.enrollmentCount} student{classPayment.enrollmentCount === 1 ? "" : "s"}
            </span>
            <span aria-hidden="true">x</span>
            <span>{formatCurrency(classPayment.price)}</span>
            <span aria-hidden="true">=</span>
            <span className="font-medium text-foreground">{formatCurrency(classPayment.totalAmount)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

const columns: ColumnDef<AdminTeacherPaymentDTO>[] = [
  {
    header: "Teacher Name",
    accessorKey: "teacherName",
  },
  {
    header: "Classes",
    cell: ({ row }) => <TeacherPaymentClasses classes={row.original.classes} />,
  },
  {
    header: "Payment Due",
    accessorFn: (row) => formatCurrency(row.paymentDue),
  },
];

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: currentAndNextSemester } = useAdminOrganizationGetCurrentAndActiveSemester(organizationId);

  const semesterId =
    (search.semesterId ?? currentAndNextSemester?.nextSemesterId ?? currentAndNextSemester?.activeSemesterId) ||
    undefined;

  const defaultFormValues = {
    ...search,
    semesterId,
  };

  const getFamilyBalances = adminTeacherPaymentsGetTeacherPayments;

  return (
    <div className="box-border flex min-h-0 w-[calc(100%-2rem)] min-w-0 max-w-full flex-1 flex-col overflow-x-hidden p-4 md:w-full md:p-6">
      <PortalPageHeader className="mb-8" title="Teacher Payments" />

      <DataTable
        columns={columns}
        renderFilters={
          <Form<{ semesterId?: number }>
            useFormProps={{ defaultValues: defaultFormValues }}
            onChange={(data) => navigate({ to: ".", search: data })}
            containerClassName="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:gap-4 md:space-y-0"
          >
            <OrganizationSemestersComboboxFormField
              className="w-full md:w-80"
              name="semesterId"
              organizationId={organizationId}
            />
          </Form>
        }
        missingRequiredFilterValues={!semesterId}
        missingRequiredFilterValuesMessage="Please select a semester."
        queryFn={(queryOptions, signal, filters) =>
          getFamilyBalances(organizationId, semesterId!, filters ?? [], queryOptions, signal)
        }
        queryKeyFn={(params, filters) => ["teacher-payments", organizationId, semesterId!, params, filters]}
        getRowIdFn={(row) => row.organizationUserId.toString()}
      />
    </div>
  );
}
